import React from 'react';
import { Button, Select, TextField, Typography } from '../../design-system';
import {
  SwHeightDefaults,
  SwHeightEquationType
} from '../../@types/swHeight/types';
import { SwHeightPatchApi } from './useSwHeightSettingsPatch';
import {
  DEFAULT_EQUATION,
  EQUATION_OPTIONS,
  PARAMETER_LABELS,
  PARAMETER_ROWS,
  SECTION_TITLES,
  numberOrNull
} from './swHeightSettings';
import styles from './SwHeightParametersPanel.module.css';

export interface SwHeightParametersPanelProps {
  api: SwHeightPatchApi;
  defaults: SwHeightDefaults | null;
  /** Render the group's own heading. Off when a drawer title carries it. */
  showHeader?: boolean;
  disabled?: boolean;
}

/**
 * The tendency fits behind the `Sw (model)` curve: one row per parameter,
 * each a (p1, p2) pair plus the fitted form. Which rows appear depends on the
 * method — Liao derives lambda, so it shows a / b / Pc max instead.
 */
export const SwHeightParametersPanel: React.FC<
  SwHeightParametersPanelProps
> = ({ api, defaults, showHeader = true, disabled = false }) => {
  const { settings, method, patchParameter, resetParameter } = api;

  return (
    <div className={styles.panel} data-testid="parameter-rows">
      {showHeader && (
        <Typography.Text weight="semibold">
          {SECTION_TITLES.parameters}
        </Typography.Text>
      )}
      {PARAMETER_ROWS[method].map(row => {
        const pair = settings[row.key];
        const fallback = defaults?.[row.defaultsKey] ?? null;
        const labels =
          PARAMETER_LABELS[pair?.equation_type ?? DEFAULT_EQUATION];
        return (
          <div
            key={row.key}
            className={styles.parameterRow}
            data-testid={`${row.key}-row`}
          >
            <div className={styles.parameterLabel}>
              <Typography.Text weight="semibold">{row.label}</Typography.Text>
              <span
                className={
                  pair?.source === 'manual'
                    ? styles.badgeManual
                    : styles.badgeFit
                }
                data-testid={`${row.key}-source`}
              >
                {pair?.source === 'manual' ? 'manual' : 'fit'}
              </span>
            </div>
            <TextField
              label={labels.p1}
              size="sm"
              type="number"
              value={pair?.p1 ?? ''}
              disabled={disabled}
              data-testid={`${row.key}-p1`}
              onChange={event =>
                patchParameter(row.key, {
                  p1: numberOrNull(event.target.value) ?? 0
                })
              }
            />
            <TextField
              label={labels.p2}
              size="sm"
              type="number"
              value={pair?.p2 ?? ''}
              disabled={disabled}
              data-testid={`${row.key}-p2`}
              onChange={event =>
                patchParameter(row.key, {
                  p2: numberOrNull(event.target.value) ?? 0
                })
              }
            />
            <Select
              label="Equation"
              size="sm"
              options={EQUATION_OPTIONS}
              value={pair?.equation_type ?? 'linear'}
              disabled={disabled}
              onChange={value =>
                patchParameter(row.key, {
                  equation_type: value as SwHeightEquationType
                })
              }
            />
            <Button
              variant="secondary"
              size="sm"
              disabled={disabled || !fallback}
              data-testid={`${row.key}-reset`}
              onClick={() => resetParameter(row.key, fallback)}
            >
              Reset to fit
            </Button>
          </div>
        );
      })}
    </div>
  );
};

export default SwHeightParametersPanel;
